import { CustomQueryOptions } from '@/types/query';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listSSHInstances, terminateSSHInstance, stopSSHInstance, resumeSSHInstance, fetchTaskLogs, validateModel, getModelSupportMatrix } from '.';
import { SSHInstance, TaskLogsResponse, ValidateModelPayload, ValidateModelResponse, ModelSupportMatrixResponse, ModelSupportMatrixError, isModelSupportMatrixError } from './type';
import { useGPUInstanceStore } from '@/store/gpu-instance';
import { useDemoMode } from '@/context/DemoModeContext';
import {
  mockSSHInstances,
  mockTerminateSSHResponse,
  mockStopSSHResponse,
  mockResumeSSHResponse,
  mockDelay,
  mockTaskLogs,
} from '@/mock';

const keyname = 'gpu-instance';

export const useListSSHInstances = (
  option?: CustomQueryOptions<SSHInstance[]>
) => {
  const { token } = useGPUInstanceStore();
  const { isDemoMode } = useDemoMode();

  return useQuery({
    queryKey: [keyname, 'ssh-list', { isDemoMode }],
    queryFn: async () => {
      if (isDemoMode) {
        await mockDelay(500);
        return mockSSHInstances;
      }

      if (!token) {
        throw new Error('No token available');
      }
      const response = await listSSHInstances(token);
      return response.instances;
    },
    enabled: isDemoMode || !!token,
    ...option,
  });
};

export const useTerminateSSHInstance = () => {
  const { token } = useGPUInstanceStore();
  const queryClient = useQueryClient();
  const { isDemoMode } = useDemoMode();

  return useMutation({
    mutationFn: async (taskId: string) => {
      if (isDemoMode) {
        await mockDelay(500);
        return mockTerminateSSHResponse(taskId);
      }

      if (!token) {
        throw new Error('No token available');
      }
      return terminateSSHInstance(taskId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keyname, 'ssh-list'] });
    },
  });
};

export const useStopSSHInstance = () => {
  const { token } = useGPUInstanceStore();
  const queryClient = useQueryClient();
  const { isDemoMode } = useDemoMode();

  return useMutation({
    mutationFn: async (taskId: string) => {
      if (isDemoMode) {
        await mockDelay(500);
        return mockStopSSHResponse(taskId);
      }

      if (!token) {
        throw new Error('No token available');
      }
      return stopSSHInstance(taskId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keyname, 'ssh-list'] });
    },
  });
};

export const useResumeSSHInstance = () => {
  const { token } = useGPUInstanceStore();
  const queryClient = useQueryClient();
  const { isDemoMode } = useDemoMode();

  return useMutation({
    mutationFn: async (taskId: string) => {
      if (isDemoMode) {
        await mockDelay(500);
        return mockResumeSSHResponse(taskId);
      }

      if (!token) {
        throw new Error('No token available');
      }
      return resumeSSHInstance(taskId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keyname, 'ssh-list'] });
    },
  });
};

export const useTaskLogs = (
  taskId: string,
  options?: {
    limit?: number;
    start?: string;
    enabled?: boolean;
    refetchInterval?: number | false;
  }
) => {
  const { token } = useGPUInstanceStore();
  const { isDemoMode } = useDemoMode();

  return useQuery<TaskLogsResponse>({
    queryKey: [keyname, 'task-logs', taskId, { limit: options?.limit, start: options?.start, isDemoMode }],
    queryFn: async () => {
      if (isDemoMode) {
        await mockDelay(300);
        return mockTaskLogs(taskId, options?.limit, options?.start);
      }

      if (!token) {
        throw new Error('No token available');
      }
      return fetchTaskLogs(taskId, token, { limit: options?.limit, start: options?.start });
    },
    enabled: (isDemoMode || !!token) && !!taskId && (options?.enabled !== false),
    refetchInterval: options?.refetchInterval,
  });
};

export const useValidateModel = () => {
  const { token } = useGPUInstanceStore();
  const { isDemoMode } = useDemoMode();

  return useMutation<ValidateModelResponse, Error, ValidateModelPayload>({
    mutationFn: async (payload) => {
      if (isDemoMode) {
        // In demo mode, return an error indicating backend is not available
        await mockDelay(500);
        return {
          status: 'error',
          error: {
            code: 'NETWORK_ERROR' as const,
            message: 'Backend not available in demo mode. Model validation requires the Python endpoint.',
          },
        };
      }

      if (!token) {
        throw new Error('No token available');
      }
      return validateModel(payload, token);
    },
  });
};

/**
 * Hook to get model support matrix from HuggingFace repository
 * Uses the GET /model/support-matrix endpoint
 */
export const useModelSupportMatrix = () => {
  const { token } = useGPUInstanceStore();
  const { isDemoMode } = useDemoMode();

  return useMutation<ModelSupportMatrixResponse | ModelSupportMatrixError, Error, string>({
    mutationFn: async (repo: string) => {
      if (isDemoMode) {
        // In demo mode, simulate a successful response for demonstration
        await mockDelay(800);
        return {
          error: 'Backend not available in demo mode',
          repo,
        } as ModelSupportMatrixError;
      }

      if (!token) {
        throw new Error('No token available');
      }
      return getModelSupportMatrix(repo, token);
    },
  });
};
